import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Mail, MapPin, Facebook, ChevronsRight, Menu, ArrowUp } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "./ui/sheet";
import { NAV_LINKS, SITE } from "../mock";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [showTop, setShowTop] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 80);
      setShowTop(window.scrollY > 500);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const linkClass = ({ isActive }) =>
    `relative text-[15px] font-semibold transition-colors duration-200 py-2 ${
      isActive ? "text-[#4BAF47]" : "text-[#24231D] hover:text-[#4BAF47]"
    } after:absolute after:left-0 after:-bottom-0.5 after:h-[2px] after:bg-[#4BAF47] after:transition-[width] after:duration-300 ${
      isActive ? "after:w-full" : "after:w-0 hover:after:w-full"
    }`;

  return (
    <>
      {/* Top bar */}
      <div className="bg-[#4BAF47] text-white">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8 h-14 flex items-center justify-between text-[13px] font-medium">
          <div className="flex items-center gap-6">
            <a href={`mailto:${SITE.email}`} className="flex items-center gap-2 hover:opacity-80 transition-opacity">
              <Mail size={14} /> <span>{SITE.email}</span>
            </a>
            <span className="hidden sm:flex items-center gap-2">
              <MapPin size={14} /> {SITE.location}
            </span>
          </div>
          <a
            href={SITE.facebook}
            target="_blank"
            rel="noreferrer"
            aria-label="Facebook"
            className="hover:opacity-80 transition-opacity"
          >
            <Facebook size={16} />
          </a>
        </div>
      </div>

      {/* Main nav - overlaps the green bar like the original */}
      <header className={`sticky top-0 z-40 transition-shadow duration-300 ${scrolled ? "shadow-[0_6px_30px_-12px_rgba(36,35,29,0.25)] bg-white" : ""}`}>
        <div className={`${scrolled ? "" : "bg-gradient-to-b from-[#4BAF47] from-50% to-transparent to-50%"}`}>
          <div className="max-w-[1320px] mx-auto px-5 lg:px-8">
            <div className={`bg-white rounded-t-md flex items-center justify-between h-[88px] ${scrolled ? "" : "px-0"}`}>
              <Link to="/" className="flex items-center" aria-label="EnviroJustice PR home">
                <img src={SITE.logo} alt="EnviroJustice PR" className="h-[62px] w-auto object-contain" />
              </Link>

              <nav className="hidden lg:flex items-center gap-9">
                {NAV_LINKS.map((l) => (
                  <NavLink key={l.path} to={l.path} end={l.path === "/"} className={linkClass}>
                    {l.label}
                  </NavLink>
                ))}
              </nav>

              <div className="flex items-center gap-3">
                <a href={SITE.facebook} target="_blank" rel="noreferrer" className="btn-green hidden md:inline-flex text-[15px] py-3">
                  Join Our FB Community <ChevronsRight size={16} />
                </a>

                <Sheet>
                  <SheetTrigger asChild>
                    <button className="lg:hidden p-2 rounded-md border border-[#e8e6dc] text-[#24231D] hover:bg-[#F8F7F0] transition-colors" aria-label="Open menu">
                      <Menu size={22} />
                    </button>
                  </SheetTrigger>
                  <SheetContent side="right" className="w-[300px] bg-white">
                    <div className="mt-6 flex flex-col gap-1">
                      <img src={SITE.logo} alt="EnviroJustice PR" className="h-14 w-auto object-contain self-start mb-6" />
                      {NAV_LINKS.map((l) => (
                        <SheetClose asChild key={l.path}>
                          <NavLink
                            to={l.path}
                            end={l.path === "/"}
                            className={({ isActive }) =>
                              `px-3 py-3 rounded-md text-base font-semibold transition-colors ${
                                isActive ? "bg-[#4BAF47]/10 text-[#4BAF47]" : "text-[#24231D] hover:bg-[#F8F7F0]"
                              }`
                            }
                          >
                            {l.label}
                          </NavLink>
                        </SheetClose>
                      ))}
                      <a href={SITE.facebook} target="_blank" rel="noreferrer" className="btn-green mt-6 justify-center">
                        Join Our FB Community <ChevronsRight size={16} />
                      </a>
                    </div>
                  </SheetContent>
                </Sheet>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Back to top */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        aria-label="Back to top"
        className={`fixed right-6 bottom-6 z-40 h-11 w-11 rounded-md bg-[#4BAF47] text-white shadow-lg flex items-center justify-center transition-opacity duration-300 hover:bg-[#3f9a3c] ${
          showTop ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      >
        <ArrowUp size={18} />
      </button>
    </>
  );
}
